import {
  Card,
  DataDisplay,
  Grid,
  Paper,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@compass/core";
import {
  SurveyReportAnswer,
  SurveyReportQuestion,
  SurveyReportResponse,
} from "generated/openApi";

import { useDispatch } from "react-redux";

export const SurveyReportReportResult = (props: {
  surveyReportResult: SurveyReportResponse;
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();

  const renderAnswer = (answer: SurveyReportAnswer, index: number) => {

    return (
      <TableRow key={`answer-${index}`}>
        <TableCell align="left">{answer.answer}</TableCell>
        <TableCell align="right">{answer.count}</TableCell>
        <TableCell align="right">{answer.percent?.toFixed(1)}%</TableCell>
      </TableRow>
    );
  };

  const renderTotal = (question: SurveyReportQuestion) => {
    return (
      <TableRow>
        <TableCell align="left" sx={{ fontWeight: 'bold' }}>Total Responses</TableCell>
        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{question.totalResponse?.count}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{question.totalResponse?.percent?.toFixed(1)}%</TableCell>
      </TableRow>
    );
  };

  const renderOtherAnswer = (answer: SurveyReportAnswer, index: number) => {
    return (
      <TableRow key={`other-answer-${index}`}>
        <TableCell align="left">{answer.answer}</TableCell>
        <TableCell align="right">{answer.count}</TableCell>
        <TableCell align="right"></TableCell>
      </TableRow>
    );
  };

  const renderGrandTotal = (question: SurveyReportQuestion) => {
    return (
      <TableRow>
        <TableCell align="left" sx={{ fontWeight: 'bold' }}>Total</TableCell>
        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{question.total?.count}</TableCell>
        <TableCell align="right"></TableCell>
      </TableRow>
    );
  };

  const renderQuestion = (question: SurveyReportQuestion, index: number) => {
    const answers = question.answers?.filter(a => a.count > 0) ?? [];
    const otherAnswers = question.otherAnswers?.filter(a => a.count > 0) ?? [];
    const hasAnswers = answers.length > 0 || otherAnswers.length > 0;

    return (
      <Grid item xs={9}>
        <Grid container spacing="sm">
          <Grid item xs={12}>
            {question.questionContent && (<Typography fontWeight={theme.typography.fontWeightBold} sx={{ marginTop: "1em" }}>{`${question?.label}. ${question.questionContent}`}</Typography>)}
            {!question.questionContent && (<Typography fontWeight={theme.typography.fontWeightBold} sx={{ marginTop: "1em" }}>{question?.label}</Typography>)}
          </Grid>
          {hasAnswers && (
            <Grid item xs={8}>
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell align="left" colorOverride="white" sx={{ backgroundColor: theme.palette.primary.main }}>Answer</TableCell>
                      <TableCell align="center" colorOverride="white" sx={{ backgroundColor: theme.palette.primary.main }}>Count #</TableCell>
                      <TableCell align="center" colorOverride="white" sx={{ backgroundColor: theme.palette.primary.main }}>Percent %</TableCell>
                    </TableRow>
                    {answers.map((a, index) => renderAnswer(a, index))}
                    {renderTotal(question)}
                    {otherAnswers.map((a, index) => renderOtherAnswer(a, index))}
                    {otherAnswers.length > 0 && renderGrandTotal(question)}
                  </TableHead>
                </Table>
              </TableContainer>
              {question.isMa && (
                <Typography fontStyle="italic" sx={{ marginTop: "0.5em" }}>
                  Note: Since respondents are able to select more than one option, the total percent may not equal 100. Percentages are based on the total number of applicable respondents, as shown.
                </Typography>
              )}
            </Grid>)}
          {question.note && (
            <Grid item xs={12}>
              <Typography>{`Note: ${question.note}`}</Typography>
            </Grid>
          )}
        </Grid>
      </Grid>
    );
  };

  const renderNote = (note: string, index: number) => {
    return (
      <Grid key={index} item xs={9}>
        <Typography fontStyle="italic">{`${note}`}</Typography>
      </Grid>
    );
  };

  return (
    <Card header={"Survey"}>
      <Grid container spacing="sm">
        <Grid item xs={12}>
          <Grid direction="row" container spacing="lg">
            <Grid item xs={11}>
              <DataDisplay label="Survey Report">{`${props.surveyReportResult.reportTitle}`}</DataDisplay>
            </Grid>
            {props.surveyReportResult.notes?.length! > 0 && props.surveyReportResult.notes?.map((n, index) => renderNote(n, index))}
          </Grid>
        </Grid>

        {props.surveyReportResult.questions?.length! > 0 && props.surveyReportResult.questions?.map((q, index) => renderQuestion(q, index))}
      </Grid>
    </Card>
  );
};
